import store from '../../../services/store';

type UmFetchOptions = RequestInit & {
    targetUserId?: string;
    tab?: string;
};

const UM_FRONT_AUDIT_URL = '/api-py/um-front-audit';

function parseRequestBody(body?: BodyInit | null): any {
    if (!body) {
        return null;
    }

    if (typeof body === 'string') {
        try {
            return JSON.parse(body);
        } catch (error) {
            return body;
        }
    }

    return null;
}

function resolveTargetUserId(
    url: string,
    requestData: any,
    explicitTargetUserId?: string
): string | null {
    if (explicitTargetUserId) {
        return String(explicitTargetUserId);
    }

    const targetUserId =
        requestData?.target_user_id ||
        requestData?.user_id ||
        requestData?.userId;

    if (targetUserId) {
        return String(targetUserId);
    }

    // Для /clone-user-data/{source}/{target}
    const cloneMatch = url.match(
        /clone-user-data\/[^/]+\/([^/?]+)/
    );

    if (cloneMatch && cloneMatch[1]) {
        return decodeURIComponent(cloneMatch[1]);
    }

    return null;
}

function resolveUmTab(url: string, explicitTab?: string): string {
    if (explicitTab) {
        return explicitTab;
    }

    // Сначала более длинные адреса
    if (url.includes('save-user-workplace-kgkp')) {
        return 'user_workplace_kgkp_link';
    }

    if (url.includes('save-user-workplace')) {
        return 'user_workplace_link';
    }

    if (url.includes('save-user-kato')) {
        return 'user_kato_link';
    }

    if (url.includes('save-user-kat')) {
        return 'user_kat_link';
    }

    if (url.includes('save-user-fgr')) {
        return 'user_fgr_link';
    }

    if (url.includes('save-user-abp')) {
        return 'user_abp_link';
    }

    if (url.includes('save-user-region')) {
        return 'user_region_link';
    }

    if (url.includes('save-user-msu')) {
        return 'user_region_link';
    }

    if (url.includes('save-user-gu')) {
        return 'user_gu_link';
    }

    if (url.includes('save-user-kgkp')) {
        return 'user_kgkp_link';
    }

    if (
        url.includes('save-user-modules') ||
        url.includes('module/link/operations/save')
    ) {
        return 'user_modules_link';
    }

    if (url.includes('clone-user-data')) {
        return 'user_management_clone';
    }

    return 'user_management';
}

function shouldWriteFrontAudit(
    url: string,
    method: string
): boolean {
    // Сам аудит повторно не логируем
    if (url.includes(UM_FRONT_AUDIT_URL)) {
        return false;
    }

    const modifyingMethods = [
        'POST',
        'PUT',
        'PATCH',
        'DELETE'
    ];

    if (
        modifyingMethods.includes(method) &&
        (
            url.includes('/api-py/save-user-') ||
            url.includes('/api-py/module/link/operations/save')
        )
    ) {
        return true;
    }

    // Клонирование сейчас GET, но изменяет данные
    return (
        method === 'GET' &&
        url.includes('/api-py/clone-user-data/')
    );
}

function createAuditHeaders(
    originalHeaders?: HeadersInit
): Headers {
    const headers = new Headers(originalHeaders || {});

    if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    if (!headers.has('Authorization')) {
        const token = store.getters.token;

        if (token) {
            headers.set(
                'Authorization',
                `Bearer ${token}`
            );
        }
    }

    return headers;
}

export async function umFetch(
    input: RequestInfo,
    options: UmFetchOptions = {}
): Promise<Response> {
    const url =
        typeof input === 'string'
            ? input
            : input.url;

    const method = String(
        options.method || 'GET'
    ).toUpperCase();

    const requestData = parseRequestBody(options.body);

    if (shouldWriteFrontAudit(url, method)) {
        const targetUserId = resolveTargetUserId(
            url,
            requestData,
            options.targetUserId
        );

        if (targetUserId) {
            try {
                const auditResponse = await fetch(
                    UM_FRONT_AUDIT_URL,
                    {
                        method: 'POST',
                        headers: createAuditHeaders(options.headers),
                        credentials: options.credentials,
                        body: JSON.stringify({
                            target_user_id: targetUserId,
                            tab: resolveUmTab(url, options.tab),
                            method,
                            endpoint: url,
                            request_data: requestData
                        })
                    }
                );

                if (!auditResponse.ok) {
                    console.error(
                        'UM frontend audit error:',
                        auditResponse.status,
                        auditResponse.statusText
                    );
                }
            } catch (error) {
                // Ошибка аудита не должна блокировать основное действие
                console.error(
                    'UM frontend audit request failed:',
                    error
                );
            }
        } else {
            console.warn(
                'UM frontend audit: target user ID not found',
                url
            );
        }
    }

    // Удаляем наши служебные параметры,
    // чтобы они не попали в обычный fetch
    const {
        targetUserId,
        tab,
        ...fetchOptions
    } = options;

    return fetch(input, fetchOptions);
}